/**
 * The **order preview** contract (P5).
 *
 * This is the one read-only addition P5 needed: the terminal must show a
 * trader, *before* they submit, the numbers the server would use — required
 * margin, commission, free margin after — without reimplementing doc 03 in a
 * browser. It is an HTTP JSON endpoint on the trade service (`POST /preview`),
 * not a control frame, because the preview is not a command: it creates no
 * order, emits no event, takes no `cl_ord_id` and never touches the journal.
 *
 * ## Why the shapes live here
 *
 * The terminal and `services/trade` must agree on this contract exactly like
 * they agree on the envelope, so it is defined once in `@weke/protocol` and
 * both import it. The numbers are the same canonical decimal strings as
 * everywhere else (docs/10 §2.6): prices ×1e8, volumes ×1e4, money in
 * account-currency minor units. Nothing here is a JS `number` except a
 * currency digit count, a symbol digit count and a boolean.
 *
 * ## What the response contains
 *
 * One round trip returns everything the order ticket and the account panel
 * need, because all of it is derived from the same engine state at the same
 * instant — fetching the symbol list, the account snapshot and the preview
 * separately would let a trader see three different books.
 *
 * - `account`: the server's own balance/equity/used margin for the account,
 *   plus the margin-call and stop-out thresholds. `margin_level_bp` is `null`
 *   when nothing is used (the `+∞` level, doc 03 §4.4): infinity is not a
 *   number and must not be rendered as one.
 * - `symbols`: the tenant's tradable catalogue with the volume bounds the
 *   ticket has to respect (min/max/step) and the digits prices are shown at.
 * - `preview`: `null` when no order was asked about; otherwise the engine's own
 *   answer to the hypothetical command (see `services/trade/src/preview.ts`).
 */

import { z } from 'zod';

import { OrderSideWire, OrderTypeWire, TimeInForceWire } from './control.js';
import { Int64String } from './wire.js';

/** One tradable symbol, as the ticket needs it. */
export const PreviewSymbol = z.strictObject({
  id: z.uuid(),
  name: z.string().regex(/^[A-Z0-9]{3,20}$/),
  base_ccy: z.string().regex(/^[A-Z]{3}$/),
  quote_ccy: z.string().regex(/^[A-Z]{3}$/),
  /** Price decimal places for display. A digit count, never an amount. */
  digits: z.number().int().min(0).max(8),
  tick_size_e8: Int64String,
  contract_size: Int64String,
  volume_min_e4: Int64String,
  volume_max_e4: Int64String,
  volume_step_e4: Int64String,
  exec_mode: z.enum(['INSTANT', 'MARKET', 'REQUEST', 'EXCHANGE']),
  fill_policy: z.enum(['FILL_OR_KILL', 'IMMEDIATE_OR_CANCEL', 'RETURN']),
  stop_level_e8: Int64String,
  freeze_level_e8: Int64String,
  enabled: z.boolean(),
  allowed_order_types: z.array(OrderTypeWire),
});
export type PreviewSymbol = z.infer<typeof PreviewSymbol>;

/**
 * Floating P&L of one open position, **and the mark it was computed at**.
 *
 * The two fields are one evaluation, not two: `mark_e8` is the price the server
 * fed to `floatingPnl` (the close side — bid for a long, ask for a short, doc
 * 03 §2.1), so a client that renders a row's `Now` from `mark_e8` and its P&L
 * from `floating_minor` cannot show a price from the stream and a P&L from an
 * older poll (the defect P5 shipped: `Now` live, P&L snapshot).
 */
export const PositionFloating = z.strictObject({
  position_id: z.uuid(),
  floating_minor: Int64String,
  /** The mark this floating value was computed at, e8. */
  mark_e8: Int64String,
});
export type PositionFloating = z.infer<typeof PositionFloating>;

/**
 * The account as of the preview instant.
 *
 * `valuable` is false when the server could not value the whole book (a missing
 * or stale mark): `equity_minor` then falls back to the balance and
 * `valuation_issue` says why. A trader must see "prices unavailable", never a
 * plausible equity computed from an old price.
 */
export const AccountSnapshot = z.strictObject({
  account_id: z.uuid(),
  login: z.string(),
  currency: z.string().regex(/^[A-Z]{3}$/),
  /** Currency minor-unit digits for display of every `_minor` field. */
  digits: z.number().int().min(0).max(8),
  balance_minor: Int64String,
  equity_minor: Int64String,
  used_margin_minor: Int64String,
  free_margin_minor: Int64String,
  floating_minor: Int64String,
  /** Margin level in basis points of percent (10000 = 100%); null = infinite. */
  margin_level_bp: Int64String.nullable(),
  margin_call_level_bp: Int64String,
  stop_out_level_bp: Int64String,
  margin_call: z.boolean(),
  stop_out: z.boolean(),
  valuable: z.boolean(),
  valuation_issue: z.string().nullable(),
  positions: z.array(PositionFloating),
});
export type AccountSnapshot = z.infer<typeof AccountSnapshot>;

/**
 * The engine's answer to a hypothetical `PLACE_ORDER`.
 *
 * Every money field is the server's number, not a client estimate:
 * `required_margin_minor` is the *incremental* used margin (used after minus
 * used before), so marginal tiers are already accounted for and adding size to
 * an existing aggregate shows the real step-up.
 *
 * `margin_reserved` is false for a resting order in a group that does not
 * reserve (`B4`): the engine creates no exposure and reserves nothing until it
 * triggers, so required margin is zero *now* and the ticket must say so rather
 * than invent a number.
 */
export const OrderPreview = z.strictObject({
  accepted: z.boolean(),
  /** A code from the closed rejection set (doc 02 §2.9/§2.10), or null. */
  rejection_code: z.string().nullable(),
  /** The engine's reason tag (`GUARD`, `ORDER_STATE`, …), or null. */
  rejection_reason: z.string().nullable(),
  required_margin_minor: Int64String,
  commission_minor: Int64String,
  balance_delta_minor: Int64String,
  equity_before_minor: Int64String,
  equity_after_minor: Int64String,
  used_margin_before_minor: Int64String,
  used_margin_after_minor: Int64String,
  free_margin_before_minor: Int64String,
  free_margin_after_minor: Int64String,
  /**
   * The account's margin level if this order were filled/resting, in basis
   * points of percent (10 000 = 100%); null is the `+∞` level.
   *
   * Computed by the server from the *after* book (`tryAssessAccount`), so the
   * ticket never divides equity by used margin itself.
   */
  margin_level_after_bp: Int64String.nullable(),
  margin_reserved: z.boolean(),
  at: z.iso.datetime({ offset: true }),
});
export type OrderPreview = z.infer<typeof OrderPreview>;

/** The body of `POST /preview`. Omit `place` to ask for the snapshot only. */
export const PreviewRequest = z.strictObject({
  place: z
    .strictObject({
      symbol_id: z.uuid(),
      side: OrderSideWire,
      type: OrderTypeWire,
      tif: TimeInForceWire,
      volume: Int64String,
      limit_price: Int64String.optional(),
      stop_price: Int64String.optional(),
      /**
       * P12.1: the price the ticket showed when the trader pressed the button.
       *
       * The trade path measures the fill against it and refuses with
       * `E_PRICE_CHANGED` when the market moved further than the group's
       * `deviation_e8`. Optional because a preview without a price is still a
       * preview; the terminal sends it for market orders so the slippage
       * tolerance is enforced on the price the trader actually saw.
       */
      expected_price: Int64String.optional(),
      sl: Int64String.optional(),
      tp: Int64String.optional(),
    })
    .optional(),
});
export type PreviewRequest = z.infer<typeof PreviewRequest>;

/** The complete `POST /preview` response. */
export const PreviewResponse = z.strictObject({
  server_time: z.iso.datetime({ offset: true }),
  account: AccountSnapshot,
  symbols: z.array(PreviewSymbol),
  preview: OrderPreview.nullable(),
});
export type PreviewResponse = z.infer<typeof PreviewResponse>;

/** Parse and validate a preview response. Throws `ZodError`. */
export function parsePreviewResponse(body: unknown): PreviewResponse {
  return PreviewResponse.parse(body);
}
